import { useMount, useUpdateEffect } from 'ahooks';
import { Alert, Button, Col, Popconfirm, Row, Space, Steps } from 'antd';
import { FC, memo, useState } from 'react';
import { ExclamationCircle, ExclamationTriangle, Trash } from 'react-bootstrap-icons';
import { useQueryClient } from 'react-query';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { CommonDrawerTitle } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { SearchConsoleAccount, SearchConsoleAccountsQueryKeys, useDeleteSearchConsoleAccountsMutation, useGetByIdSearchConsoleAccountsQuery } from '@/modules/DomainsApi';

interface Props {
  searchConsoleAccountId: string | undefined;
}

const defineStep = (location: ReturnType<typeof useLocation>): 1 | 2 | 3 => {
  if (location.pathname.includes('/credentials')) return 2;
  if (location.pathname.includes('/oauth2')) return 3;
  return 1;
};

export const SearchConsoleAccountsFormContainer: FC<Props> = memo(({ searchConsoleAccountId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const [step, setStep] = useState<1 | 2 | 3>(defineStep(location));
  const [disableSecondStep, setDisableSecondStep] = useState(true);
  const [disableThirdStep, setDisableThirdStep] = useState(true);

  const { data: searchConsoleAccountData, refetch } = useGetByIdSearchConsoleAccountsQuery(searchConsoleAccountId ?? '')({
    enabled: !!searchConsoleAccountId
  });

  useMount(async () => {
    if (searchConsoleAccountId) {
      if (!queryClient.getQueryData<SearchConsoleAccount>([SearchConsoleAccountsQueryKeys.GetOneById, searchConsoleAccountId], {
        exact: true
      })) {
        await refetch();
      }

      const data = queryClient.getQueryData<SearchConsoleAccount>([SearchConsoleAccountsQueryKeys.GetOneById, searchConsoleAccountId], {
        exact: true
      });

      setDisableSecondStep(!(data?.email && data?.password));

      setDisableThirdStep(!(data?.clientId && data?.clientSecret) || location.pathname.includes('/edit'));
    }
  });

  useUpdateEffect(() => {
    setStep(defineStep(location));
  }, [location.pathname]);

  useUpdateEffect(() => {
    if (location.pathname.includes('/edit')) setDisableThirdStep(true);
    else setDisableThirdStep(false);
  }, [location.pathname]);

  useUpdateEffect(() => {
    if (searchConsoleAccountId) {
      const data = queryClient.getQueryData<SearchConsoleAccount>([SearchConsoleAccountsQueryKeys.GetOneById, searchConsoleAccountId], {
        exact: true
      });

      setDisableSecondStep(!(data?.email && data?.password));

      setDisableThirdStep(!(data?.clientId && data?.clientSecret));
    } else {
      setDisableSecondStep(true);
      setDisableThirdStep(true);
    }
  }, [searchConsoleAccountId]);

  const { isLoading: isDeleteSearchConsoleAccountLoading, mutate: deleteSearchConsoleAccountMutation } =
    useDeleteSearchConsoleAccountsMutation(searchConsoleAccountId || '')({
      onSuccess: async () => {
        appMessageApi.success('Search Console акаунт успішно видалено!');

        await queryClient.refetchQueries([SearchConsoleAccountsQueryKeys.GetAll]);

        navigate(AppRoutes.DomainsApiSearchConsoleAccounts);
      },
      onError: (error) => {
        messageOnError(error, appMessageApi);
      }
    });

  const handleDeleteAccount = () => {
    deleteSearchConsoleAccountMutation();
  };

  const onChange = (changedStep: number) => {
    ++changedStep;

    if (changedStep === 1) {
      navigate(AppRoutes.DomainsApiSearchConsoleAccountsEdit.replace(':searchConsoleAccountId', searchConsoleAccountId ?? ''));
    } else if (changedStep === 2) {
      navigate(AppRoutes.DomainsApiSearchConsoleAccountsEditCreds.replace(
        ':searchConsoleAccountId',
        searchConsoleAccountId || '')
      );
    } else {
      navigate(AppRoutes.DomainsApiSearchConsoleAccountsCreateOauth2.replace(
        ':searchConsoleAccountId',
        searchConsoleAccountId || '')
      );
    }
  };

  return (
    <>
      <CommonDrawerTitle
        onlyExtra
        extra={
          <Space>
            {
              !!searchConsoleAccountId &&
              <Popconfirm
                disabled={ isDeleteSearchConsoleAccountLoading }
                title="Видалити цей Search Console акаунт?"
                onConfirm={ handleDeleteAccount }
                okType="danger"
                okText="Так"
                icon={ <ExclamationCircle/> }
                placement="bottomRight"
              >
                <Button
                  loading={ isDeleteSearchConsoleAccountLoading }
                  disabled={ isDeleteSearchConsoleAccountLoading }
                  danger
                  icon={ <Trash/> }
                >
                  Видалити
                </Button>
              </Popconfirm>
            }
          </Space>
        }
      />

      <Row gutter={ [0, 16] }>
        <Col span={ 24 }>
          <Steps
            size="small"
            responsive
            current={ step - 1 }
            items={ [
              { title: 'Авторизація' },
              { title: 'Облікові дані', disabled: disableSecondStep && step < 2 },
              { title: 'OAuth2', disabled: disableThirdStep && step < 3 }] }
            onChange={ onChange }
          />
        </Col>

        {
          location.state?.shouldAlert && <Col span={ 24 }>
            <Alert
              showIcon
              icon={ <ExclamationTriangle/> }
              type="warning"
              message="Завершіть налаштування акаунту Search Console"
            />
          </Col>
        }

        {
          searchConsoleAccountData?.refreshTokenError && <Col span={ 24 }>
            <Alert
              showIcon
              type="error"
              message="Токен перестав працювати"
              description={ searchConsoleAccountData.refreshTokenError }
            />
          </Col>
        }

        <Col span={ 24 }>
          <Outlet/>
        </Col>
      </Row>
    </>
  );
});
